<template>
  <div class="dashboard-grid">
    <!-- Left menu -->
    <aside class="dashboard-sidebar">
      <v-list nav density="comfortable">
        <!-- Home / My Bookings (role-based) -->
        <v-list-item v-if="isAdmin || isAgent" :to="toPath('home')" :class="{ 'active-link': isActive(toPath('home')) }"
          rounded="lg">
          <v-list-item-title>Home</v-list-item-title>
        </v-list-item>

        <v-list-item v-if="isAgent" :to="toPath('post-listing')"
          :class="{ 'active-link': isActive(toPath('post-listing')) }" rounded="lg">
          <v-list-item-title>Post New Listing</v-list-item-title>
        </v-list-item>

        <v-list-item v-else :to="toPath('bookings')" :class="{ 'active-link': isActive(toPath('bookings')) }"
          rounded="lg">
          <v-list-item-title>My Bookings</v-list-item-title>
        </v-list-item>

        <v-list-item :to="toPath('profile')" :class="{ 'active-link': isActive(toPath('profile')) }" rounded="lg">
          <v-list-item-title>My Profile</v-list-item-title>
        </v-list-item>

        <v-list-item :to="toPath('messages')" :class="{ 'active-link': isActive(toPath('messages')) }" rounded="lg">
          <v-list-item-title class="d-flex justify-space-between align-center">
            Messages
            <v-badge v-if="unreadCount > 0" color="error" :content="unreadCount" inline />
          </v-list-item-title>
        </v-list-item>

        <v-list-item :to="toPath('notifications')" :class="{ 'active-link': isActive(toPath('notifications')) }"
          rounded="lg">
          <v-list-item-title class="d-flex justify-space-between align-center">
            Notifications
            <v-badge v-if="notificationCount > 0" color="error" :content="notificationCount" inline />
          </v-list-item-title>
        </v-list-item>

        <v-list-item v-if="isAdmin" :to="toPath('listings')" :class="{ 'active-link': isActive(toPath('listings')) }"
          rounded="lg">
          <v-list-item-title>All Current Listings</v-list-item-title>
        </v-list-item>

        <v-list-item v-if="isAgent" :to="toPath('listings')" :class="{ 'active-link': isActive(toPath('listings')) }"
          rounded="lg">
          <v-list-item-title>Current Listings</v-list-item-title>
        </v-list-item>

        <v-list-item v-if="isAdmin" :to="toPath('agents')" :class="{ 'active-link': isActive(toPath('agents')) }"
          rounded="lg">
          <v-list-item-title>Current Agents</v-list-item-title>
        </v-list-item>

        <v-list-item v-if="isAdmin" :to="toPath('pendingagents')"
          :class="{ 'active-link': isActive(toPath('pendingagents')) }" rounded="lg">
          <v-list-item-title>Pending Agents</v-list-item-title>
        </v-list-item>

        <v-list-item v-if="isAdmin" :to="toPath('email-templates')"
          :class="{ 'active-link': isActive(toPath('email-templates')) }" rounded="lg">
          <v-list-item-title>Email Templates</v-list-item-title>
        </v-list-item>
      </v-list>
    </aside>


    <!-- Right content -->
    <section class="dashboard-content">
      <router-view :key="$route.fullPath" />
    </section>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { supabase } from '@/supabaseClient'

export default {
  name: 'DashboardShell',
  data: () => ({
    unreadCount: 0,
    notificationCount: 0,
    notifChannel: null,
    chatChannel: null,
  }),
  setup() {
    const store = useStore()
    const userData = computed(() => store.state.user)
    return { userData }
  },
  computed: {
    isAdmin() { return this.userData?.isAdmin ?? false },
    isAgent() { return this.userData?.is_agent ?? false },
  },
  async mounted() {
    await this.refreshCounts()
    this.subscribeRealtime()
  },
  beforeUnmount() {
    if (this.notifChannel) supabase.removeChannel(this.notifChannel)
    if (this.chatChannel) supabase.removeChannel(this.chatChannel)
  },
  methods: {
    async refreshCounts() {
      await Promise.all([this.getMessageCount(), this.getNotificationCount()])
    },
    async getMessageCount() {
      const receiverId = this.userData?.uid || this.userData?.id
      if (!receiverId) { this.unreadCount = 0; return }
      const { count } = await supabase
        .from('chats')
        .select('*', { count: 'exact', head: true })
        .eq('receiver_id', receiverId)
        .is('read_at', null)
      this.unreadCount = count || 0
    },
    async getNotificationCount() {
      const uid = this.userData?.uid || this.userData?.id
      if (!uid) { this.notificationCount = 0; return }
      const { count } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', uid)
        .is('read_at', null)
      this.notificationCount = count || 0
    },

    // 🔔 realtime
    subscribeRealtime() {
      const uid = this.userData?.uid || this.userData?.id
      if (!uid) return

      // clean up first
      if (this.notifChannel) supabase.removeChannel(this.notifChannel)
      if (this.chatChannel) supabase.removeChannel(this.chatChannel)

      // Notifications feed
      this.notifChannel = supabase
        .channel(`notif-count:${uid}`)
        .on('postgres_changes', {
          event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${uid}`
        }, (payload) => this.applyNotifDelta(payload))
        .subscribe()

      // Chats feed
      this.chatChannel = supabase
        .channel(`chat-count:${uid}`)
        .on('postgres_changes', {
          event: '*', schema: 'public', table: 'chats', filter: `receiver_id=eq.${uid}`
        }, (payload) => this.applyChatDelta(payload))
        .subscribe()
    },

    // Incremental counters
    applyNotifDelta(payload) {
      const evt = payload.eventType // 'INSERT' | 'UPDATE' | 'DELETE'
      const n = payload.new || {}
      const o = payload.old || {}

      if (evt === 'INSERT') {
        if (!n.read_at) this.notificationCount++
      } else if (evt === 'UPDATE') {
        const wasUnread = !o.read_at
        const isUnread = !n.read_at
        if (wasUnread && !isUnread) this.notificationCount = Math.max(0, this.notificationCount - 1)
        else if (!wasUnread && isUnread) this.notificationCount++
      } else if (evt === 'DELETE') {
        if (!o.read_at) this.notificationCount = Math.max(0, this.notificationCount - 1)
      }
    },

    applyChatDelta(payload) {
      const evt = payload.eventType
      const n = payload.new || {}
      const o = payload.old || {}

      if (evt === 'INSERT') {
        if (!n.read_at) this.unreadCount++
      } else if (evt === 'UPDATE') {
        const wasUnread = !o.read_at
        const isUnread = !n.read_at
        if (wasUnread && !isUnread) this.unreadCount = Math.max(0, this.unreadCount - 1)
        else if (!wasUnread && isUnread) this.unreadCount++
      } else if (evt === 'DELETE') {
        if (!o.read_at) this.unreadCount = Math.max(0, this.unreadCount - 1)
      }
    },

    toPath(view) {
      const map = {
        home: 'home',
        profile: 'profile',
        messages: 'messages',
        notifications: 'notifications',
        bookings: 'bookings',
        'post-listing': 'post-listing',
        listings: 'listings',
        agents: 'agents',
        pendingagents: 'pending-agents',
        'email-templates': 'email-templates'
      }
      return `/dashboard/${map[view] || 'home'}`
    },

    isActive(path) {
      // highlight for exact or nested routes under the same section
      return this.$route.path === path || this.$route.path.startsWith(path + '/')
    },
  },
  watch: {
    // if the logged-in user changes (login/logout), re-init
    userData: {
      deep: true,
      immediate: false,
      async handler() {
        await this.refreshCounts()
        this.subscribeRealtime()
      }
    }
  }
}
</script>

<style scoped>
/* Two-column split just for this subpage */
.dashboard-grid {
  display: grid;
  grid-template-columns: 180px 1fr;
  /* sidebar width + content */
  gap: 0;
  min-height: calc(100vh - 64px);
  /* adjust if your top app-bar height differs */
  background: #faf7f2;
  /* your light bg, optional */
}

.dashboard-sidebar {
  background: white;
  border-right: 1px solid #eee;
  padding: 12px 8px;
  position: sticky;
  /* keep menu fixed while content scrolls */
  top: 0;
  align-self: start;
  height: 100%;
}

.dashboard-content {
  padding: 10px;
  overflow: auto;
  /* content scrolls independently */
  background: #faf7f2;
}

.active-link {
  background: #e8f6f6;
  /* light teal highlight */
  font-weight: 600;
}
</style>
